import { Extension } from "@tiptap/core";
import { Color } from "@tiptap/extension-color";
import Highlight from "@tiptap/extension-highlight";
import Image from "@tiptap/extension-image";
import { mergeAttributes } from "@tiptap/core";
import { TextStyle } from "@tiptap/extension-text-style";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import Link from "@tiptap/extension-link";
import Subscript from "@tiptap/extension-subscript";
import Superscript from "@tiptap/extension-superscript";
import StarterKit from "@tiptap/starter-kit";
import {
  EditorContent,
  NodeViewWrapper,
  ReactNodeViewRenderer,
  useEditor,
  useEditorState,
} from "@tiptap/react";
import {
  BorderColor as HighlightIcon,
  Code as CodeIcon,
  FormatAlignCenter as FormatAlignCenterIcon,
  FormatAlignJustify as FormatAlignJustifyIcon,
  FormatAlignLeft as FormatAlignLeftIcon,
  FormatAlignRight as FormatAlignRightIcon,
  FormatBold as FormatBoldIcon,
  FormatClear as FormatClearIcon,
  FormatColorText as FormatColorTextIcon,
  FormatItalic as FormatItalicIcon,
  FormatListBulleted as FormatListBulletedIcon,
  FormatListNumbered as FormatListNumberedIcon,
  FormatQuote as FormatQuoteIcon,
  FormatStrikethrough as FormatStrikethroughIcon,
  FormatUnderlined as FormatUnderlinedIcon,
  HorizontalRule as HorizontalRuleIcon,
  Image as ImageIcon,
  Link as LinkIcon,
  LinkOff as LinkOffIcon,
  PictureAsPdf as PictureAsPdfIcon,
  RateReview as RateReviewIcon,
  Redo as RedoIcon,
  Subscript as SubscriptIcon,
  Superscript as SuperscriptIcon,
  Undo as UndoIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import AnalistaRoute from "../../components/AnalistaRoute";
import pdfLayoutService from "../../services/pdfLayout";
import { html2canvasForPdf } from "../../utils/pagedHtmlPdf";
import {
  PARECER_DEFAULT_FONT_SIZE,
  PARECER_EDITOR_PROSEMIRROR_SX,
  PARECER_PAGE_WIDTH_CSS,
  applyParecerPdfTypography,
  assembleParecerPdf,
  parseFontSizeValue,
  toFontSizeCss,
} from "../../utils/parecerTecnicoPdf";

// ─── Imagem redimensionável ───────────────────────────────────────────────────
const HANDLES = [
  { cursor: "nw-resize", top: -4, left: -4, dx: -1, dy: -1 },
  { cursor: "ne-resize", top: -4, right: -4, dx: 1, dy: -1 },
  { cursor: "sw-resize", bottom: -4, left: -4, dx: -1, dy: 1 },
  { cursor: "se-resize", bottom: -4, right: -4, dx: 1, dy: 1 },
];

function ResizableImageView({ node, updateAttributes, selected }) {
  const startX = useRef(0);
  const startY = useRef(0);
  const startW = useRef(0);
  const startH = useRef(0);
  const dirX = useRef(1);
  const dirY = useRef(1);
  const imgRef = useRef(null);
  const [resizing, setResizing] = useState(false);

  const onHandleMouseDown = useCallback(
    (e, dx, dy) => {
      e.preventDefault();
      e.stopPropagation();
      startX.current = e.clientX;
      startY.current = e.clientY;
      startW.current = imgRef.current?.offsetWidth || node.attrs.width || 300;
      startH.current = imgRef.current?.offsetHeight || node.attrs.height || 200;
      dirX.current = dx;
      dirY.current = dy;
      setResizing(true);

      const onMove = (ev) => {
        const dw = (ev.clientX - startX.current) * dirX.current;
        const dh = (ev.clientY - startY.current) * dirY.current;
        const newW = Math.max(60, Math.round(startW.current + dw));
        const newH = Math.max(40, Math.round(startH.current + dh));
        updateAttributes({ width: newW, height: newH });
      };

      const onUp = () => {
        setResizing(false);
        window.removeEventListener("mousemove", onMove);
        window.removeEventListener("mouseup", onUp);
      };

      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", onUp);
    },
    [node.attrs.width, node.attrs.height, updateAttributes],
  );

  const w = node.attrs.width;
  const h = node.attrs.height;

  return (
    <NodeViewWrapper
      style={{
        display: "inline-block",
        position: "relative",
        userSelect: resizing ? "none" : undefined,
        lineHeight: 0,
      }}
    >
      <img
        ref={imgRef}
        src={node.attrs.src}
        alt={node.attrs.alt || ""}
        width={w || undefined}
        height={h || undefined}
        style={{
          display: "block",
          maxWidth: "100%",
          outline: selected ? "2px solid #1976d2" : "none",
          outlineOffset: 1,
          cursor: "default",
        }}
        draggable={false}
      />
      {selected &&
        HANDLES.map((handle, i) => (
          <div
            key={i}
            onMouseDown={(e) => onHandleMouseDown(e, handle.dx, handle.dy)}
            style={{
              position: "absolute",
              width: 10,
              height: 10,
              backgroundColor: "#1976d2",
              border: "1px solid white",
              borderRadius: 2,
              cursor: handle.cursor,
              top: handle.top,
              left: handle.left,
              right: handle.right,
              bottom: handle.bottom,
              zIndex: 10,
            }}
          />
        ))}
    </NodeViewWrapper>
  );
}

const ResizableImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      width: {
        default: null,
        parseHTML: (el) => el.getAttribute("width") || null,
        renderHTML: (attrs) => (attrs.width ? { width: attrs.width } : {}),
      },
      height: {
        default: null,
        parseHTML: (el) => el.getAttribute("height") || null,
        renderHTML: (attrs) => (attrs.height ? { height: attrs.height } : {}),
      },
    };
  },
  renderHTML({ HTMLAttributes }) {
    return ["img", mergeAttributes(this.options.HTMLAttributes, HTMLAttributes)];
  },
  addNodeView() {
    return ReactNodeViewRenderer(ResizableImageView);
  },
});
// ──────────────────────────────────────────────────────────────────────────────

// Extensão customizada de tamanho de fonte via TextStyle
const FontSize = Extension.create({
  name: "fontSize",
  addOptions() {
    return { types: ["textStyle"] };
  },
  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          fontSize: {
            default: null,
            parseHTML: (el) => parseFontSizeValue(el.style.fontSize),
            renderHTML: (attrs) => toFontSizeCss(attrs.fontSize),
          },
        },
      },
    ];
  },
  addCommands() {
    return {
      setFontSize:
        (size) =>
        ({ chain }) =>
          chain().setMark("textStyle", { fontSize: size }).run(),
      unsetFontSize:
        () =>
        ({ chain }) =>
          chain()
            .setMark("textStyle", { fontSize: null })
            .removeEmptyTextStyle()
            .run(),
    };
  },
});

const FONT_SIZES = [8, 9, 10, 11, 12, 14, 16, 18, 20, 22, 24, 28, 32, 36, 48, 72];

const HEADING_OPTIONS = [
  { label: "Normal", value: "paragraph" },
  { label: "Título 1", value: "1" },
  { label: "Título 2", value: "2" },
  { label: "Título 3", value: "3" },
];

function ToolbarBtn({ title, onClick, active, disabled, children }) {
  const theme = useTheme();
  return (
    <Tooltip title={title} arrow>
      <span>
        <IconButton
          size="small"
          onClick={onClick}
          disabled={disabled}
          aria-label={title}
          sx={{
            borderRadius: 1,
            color: active ? theme.palette.primary.main : "text.primary",
            backgroundColor: active
              ? `${theme.palette.primary.main}1a`
              : "transparent",
            "&:hover": {
              backgroundColor: active
                ? `${theme.palette.primary.main}2e`
                : "action.hover",
            },
          }}
        >
          {children}
        </IconButton>
      </span>
    </Tooltip>
  );
}

function ColorBtn({ title, inputRef, color, onChange, Icon }) {
  return (
    <Tooltip title={title} arrow>
      <Box sx={{ position: "relative", display: "inline-flex" }}>
        <IconButton
          size="small"
          aria-label={title}
          sx={{ borderRadius: 1, pb: "14px" }}
          onClick={() => inputRef.current?.click()}
        >
          <Icon fontSize="small" />
        </IconButton>
        <Box
          sx={{
            position: "absolute",
            bottom: 6,
            left: "50%",
            transform: "translateX(-50%)",
            width: 16,
            height: 3,
            backgroundColor: color,
            borderRadius: 0.5,
            pointerEvents: "none",
          }}
        />
        <input
          ref={inputRef}
          type="color"
          value={color}
          onChange={onChange}
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            opacity: 0,
            pointerEvents: "none",
            border: 0,
            padding: 0,
          }}
        />
      </Box>
    </Tooltip>
  );
}

function MenuBar({ editor }) {
  const theme = useTheme();
  const textColorRef = useRef(null);
  const highlightColorRef = useRef(null);
  const imageInputRef = useRef(null);
  const savedSelectionRef = useRef(null);

  const toolbarState = useEditorState({
    editor,
    selector: ({ editor: currentEditor }) => {
      if (!currentEditor) {
        return {
          heading: "paragraph",
          fontSize: PARECER_DEFAULT_FONT_SIZE,
          textColor: "#000000",
          highlightColor: "#FFFF00",
        };
      }

      const heading = [1, 2, 3].find((level) =>
        currentEditor.isActive("heading", { level })
      );

      return {
        heading: heading ? String(heading) : "paragraph",
        fontSize:
          currentEditor.getAttributes("textStyle").fontSize ||
          PARECER_DEFAULT_FONT_SIZE,
        textColor: currentEditor.getAttributes("textStyle").color || "#000000",
        highlightColor:
          currentEditor.getAttributes("highlight").color || "#FFFF00",
      };
    },
  });

  if (!editor) return null;

  const rememberSelection = () => {
    const { from, to } = editor.state.selection;
    savedSelectionRef.current = { from, to };
  };

  const chainWithSelection = () => {
    let chain = editor.chain().focus();
    if (savedSelectionRef.current) {
      chain = chain.setTextSelection(savedSelectionRef.current);
    }
    return chain;
  };

  const handleHeadingChange = (e) => {
    const val = e.target.value;
    if (val === "paragraph") {
      chainWithSelection().setParagraph().run();
    } else {
      chainWithSelection().setHeading({ level: Number(val) }).run();
    }
  };

  const handleFontSizeChange = (e) => {
    chainWithSelection().setFontSize(String(e.target.value)).run();
  };

  const handleTextColor = (e) => {
    editor.chain().focus().setColor(e.target.value).run();
  };

  const handleHighlightColor = (e) => {
    editor.chain().focus().setHighlight({ color: e.target.value }).run();
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () =>
      editor.chain().focus().setImage({ src: reader.result }).run();
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const handleSetLink = () => {
    const prev = editor.getAttributes("link").href || "";
    const url = window.prompt("URL do link:", prev);
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().unsetLink().run();
    } else {
      editor.chain().focus().setLink({ href: url, target: "_blank" }).run();
    }
  };

  const selectSx = {
    height: 30,
    fontSize: 13,
    "& .MuiOutlinedInput-notchedOutline": { borderColor: "divider" },
  };

  return (
    <Paper
      elevation={0}
      sx={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 0.5,
        p: 1,
        overflowX: "auto",
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: "8px 8px 0 0",
        backgroundColor: "background.paper",
        position: "sticky",
        top: 64,
        zIndex: 10,
      }}
    >
      {/* Desfazer / Refazer */}
      <ToolbarBtn
        title="Desfazer (Ctrl+Z)"
        onClick={() => editor.chain().focus().undo().run()}
        disabled={!editor.can().undo()}
      >
        <UndoIcon fontSize="small" />
      </ToolbarBtn>
      <ToolbarBtn
        title="Refazer (Ctrl+Y)"
        onClick={() => editor.chain().focus().redo().run()}
        disabled={!editor.can().redo()}
      >
        <RedoIcon fontSize="small" />
      </ToolbarBtn>

      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

      {/* Parágrafo / Título */}
      <Select
        size="small"
        value={toolbarState.heading}
        onMouseDown={rememberSelection}
        onChange={handleHeadingChange}
        aria-label="Estilo de parágrafo"
        sx={{ ...selectSx, minWidth: 110 }}
      >
        {HEADING_OPTIONS.map((opt) => (
          <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: 13 }}>
            {opt.label}
          </MenuItem>
        ))}
      </Select>

      {/* Tamanho da fonte */}
      <Select
        size="small"
        value={toolbarState.fontSize}
        onMouseDown={rememberSelection}
        onChange={handleFontSizeChange}
        aria-label="Tamanho da fonte"
        sx={{ ...selectSx, minWidth: 64 }}
      >
        {FONT_SIZES.map((size) => (
          <MenuItem key={size} value={String(size)} sx={{ fontSize: 13 }}>
            {size}
          </MenuItem>
        ))}
      </Select>

      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

      {/* Negrito, Itálico, Sublinhado, Tachado */}
      <ToolbarBtn
        title="Negrito (Ctrl+B)"
        onClick={() => editor.chain().focus().toggleBold().run()}
        active={editor.isActive("bold")}
      >
        <FormatBoldIcon fontSize="small" />
      </ToolbarBtn>
      <ToolbarBtn
        title="Itálico (Ctrl+I)"
        onClick={() => editor.chain().focus().toggleItalic().run()}
        active={editor.isActive("italic")}
      >
        <FormatItalicIcon fontSize="small" />
      </ToolbarBtn>
      <ToolbarBtn
        title="Sublinhado (Ctrl+U)"
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        active={editor.isActive("underline")}
      >
        <FormatUnderlinedIcon fontSize="small" />
      </ToolbarBtn>
      <ToolbarBtn
        title="Tachado"
        onClick={() => editor.chain().focus().toggleStrike().run()}
        active={editor.isActive("strike")}
      >
        <FormatStrikethroughIcon fontSize="small" />
      </ToolbarBtn>

      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

      {/* Sobrescrito / Subscrito */}
      <ToolbarBtn
        title="Sobrescrito"
        onClick={() => editor.chain().focus().toggleSuperscript().run()}
        active={editor.isActive("superscript")}
      >
        <SuperscriptIcon fontSize="small" />
      </ToolbarBtn>
      <ToolbarBtn
        title="Subscrito"
        onClick={() => editor.chain().focus().toggleSubscript().run()}
        active={editor.isActive("subscript")}
      >
        <SubscriptIcon fontSize="small" />
      </ToolbarBtn>

      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

      {/* Cor do texto */}
      <ColorBtn
        title="Cor do texto"
        inputRef={textColorRef}
        color={toolbarState.textColor}
        onChange={handleTextColor}
        Icon={FormatColorTextIcon}
      />

      {/* Realce / Marcador */}
      <ColorBtn
        title="Realçar texto"
        inputRef={highlightColorRef}
        color={toolbarState.highlightColor}
        onChange={handleHighlightColor}
        Icon={HighlightIcon}
      />

      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

      {/* Alinhamento */}
      <ToolbarBtn
        title="Alinhar à esquerda"
        onClick={() => editor.chain().focus().setTextAlign("left").run()}
        active={editor.isActive({ textAlign: "left" })}
      >
        <FormatAlignLeftIcon fontSize="small" />
      </ToolbarBtn>
      <ToolbarBtn
        title="Centralizar"
        onClick={() => editor.chain().focus().setTextAlign("center").run()}
        active={editor.isActive({ textAlign: "center" })}
      >
        <FormatAlignCenterIcon fontSize="small" />
      </ToolbarBtn>
      <ToolbarBtn
        title="Alinhar à direita"
        onClick={() => editor.chain().focus().setTextAlign("right").run()}
        active={editor.isActive({ textAlign: "right" })}
      >
        <FormatAlignRightIcon fontSize="small" />
      </ToolbarBtn>
      <ToolbarBtn
        title="Justificar"
        onClick={() => editor.chain().focus().setTextAlign("justify").run()}
        active={editor.isActive({ textAlign: "justify" })}
      >
        <FormatAlignJustifyIcon fontSize="small" />
      </ToolbarBtn>

      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

      {/* Listas */}
      <ToolbarBtn
        title="Lista com marcadores"
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        active={editor.isActive("bulletList")}
      >
        <FormatListBulletedIcon fontSize="small" />
      </ToolbarBtn>
      <ToolbarBtn
        title="Lista numerada"
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        active={editor.isActive("orderedList")}
      >
        <FormatListNumberedIcon fontSize="small" />
      </ToolbarBtn>

      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

      {/* Citação / Código / Linha horizontal */}
      <ToolbarBtn
        title="Citação (blockquote)"
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        active={editor.isActive("blockquote")}
      >
        <FormatQuoteIcon fontSize="small" />
      </ToolbarBtn>
      <ToolbarBtn
        title="Código inline"
        onClick={() => editor.chain().focus().toggleCode().run()}
        active={editor.isActive("code")}
      >
        <CodeIcon fontSize="small" />
      </ToolbarBtn>
      <ToolbarBtn
        title="Linha horizontal"
        onClick={() => editor.chain().focus().setHorizontalRule().run()}
      >
        <HorizontalRuleIcon fontSize="small" />
      </ToolbarBtn>

      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

      {/* Link */}
      <ToolbarBtn
        title="Inserir/editar link"
        onClick={handleSetLink}
        active={editor.isActive("link")}
      >
        <LinkIcon fontSize="small" />
      </ToolbarBtn>
      <ToolbarBtn
        title="Remover link"
        onClick={() => editor.chain().focus().unsetLink().run()}
        disabled={!editor.isActive("link")}
      >
        <LinkOffIcon fontSize="small" />
      </ToolbarBtn>

      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

      {/* Inserir imagem */}
      <ToolbarBtn
        title="Inserir imagem (ou cole com Ctrl+V)"
        onClick={() => imageInputRef.current?.click()}
      >
        <ImageIcon fontSize="small" />
      </ToolbarBtn>
      <input
        ref={imageInputRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={handleImageUpload}
      />

      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

      {/* Limpar formatação */}
      <ToolbarBtn
        title="Limpar formatação"
        onClick={() =>
          editor.chain().focus().clearNodes().unsetAllMarks().run()
        }
      >
        <FormatClearIcon fontSize="small" />
      </ToolbarBtn>
    </Paper>
  );
}

const CORPO_PADRAO = `
  <h1>Parecer Técnico</h1>
  <p>Município: ______________________ &nbsp;&nbsp; Data: ______/______/______</p>
  <p>Processo nº: ______________________</p>
  <p><strong>Requerente:</strong> ______________________</p>
  <p>&nbsp;</p>
  <h2>1. Objeto</h2>
  <p>Descreva o objeto do parecer técnico.</p>
  <p>&nbsp;</p>
  <h2>2. Análise Técnica</h2>
  <p>Insira aqui a análise técnica detalhada.</p>
  <p>&nbsp;</p>
  <h2>3. Conclusão</h2>
  <p>Insira a conclusão e a recomendação técnica.</p>
  <p>&nbsp;</p>
  <p>______________________________</p>
  <p><strong>Analista Responsável</strong></p>
`;

const toBase64 = (blob) =>
  new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.readAsDataURL(blob);
  });

const ParecerTecnico = () => {
  const theme = useTheme();
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [layoutLoading, setLayoutLoading] = useState(true);
  const [layoutInfo, setLayoutInfo] = useState(null); // { id, headerBase64, footerBase64 }
  const [layoutErro, setLayoutErro] = useState(false);

  const handlePreview = async () => {
    if (!editor) return;
    setPreviewLoading(true);
    setPreviewOpen(true);

    const [{ default: jsPDF }, { default: html2canvas }] = await Promise.all([
      import("jspdf"),
      import("html2canvas"),
    ]);

    const createOffscreen = () => {
      const el = document.createElement("div");
      Object.assign(el.style, {
        position: "fixed",
        top: "-10000px",
        left: "0",
        width: PARECER_PAGE_WIDTH_CSS,
        backgroundColor: "#ffffff",
      });
      document.body.appendChild(el);
      return el;
    };

    const headerEl = createOffscreen();
    const footerEl = createOffscreen();
    const contentEl = createOffscreen();

    try {
      let headerCanvas = null;
      if (layoutInfo?.headerBase64) {
        const img = document.createElement("img");
        img.src = layoutInfo.headerBase64;
        img.style.cssText = "width:100%;display:block;";
        headerEl.appendChild(img);
        await new Promise((resolve) => {
          if (img.complete) resolve();
          else {
            img.onload = resolve;
            img.onerror = resolve;
          }
        });
        headerCanvas = await html2canvasForPdf(html2canvas, headerEl);
      }

      let footerCanvas = null;
      if (layoutInfo?.footerBase64) {
        const img = document.createElement("img");
        img.src = layoutInfo.footerBase64;
        img.style.cssText = "width:100%;display:block;";
        footerEl.appendChild(img);
        await new Promise((resolve) => {
          if (img.complete) resolve();
          else {
            img.onload = resolve;
            img.onerror = resolve;
          }
        });
        footerCanvas = await html2canvasForPdf(html2canvas, footerEl);
      }

      const body = document.createElement("div");
      body.innerHTML = editor.getHTML();
      applyParecerPdfTypography(body);
      body.querySelectorAll("img").forEach((el) => {
        const attrW = el.getAttribute("width");
        el.removeAttribute("width");
        el.removeAttribute("height");
        if (attrW) el.style.width = `${attrW}px`;
        el.style.maxWidth = "100%";
        el.style.height = "auto";
        el.style.display = "block";
      });
      contentEl.appendChild(body);

      const contentImgs = Array.from(contentEl.querySelectorAll("img"));
      await Promise.all(
        contentImgs.map(
          (img) =>
            new Promise((resolve) => {
              if (img.complete) resolve();
              else {
                img.onload = resolve;
                img.onerror = resolve;
              }
            })
        )
      );

      Array.from(contentEl.querySelectorAll("img")).forEach((img) => {
        const computed = window.getComputedStyle(img);
        const w = parseFloat(computed.width);
        const h = parseFloat(computed.height);
        if (w > 0 && h > 0) {
          img.style.width = `${Math.round(w)}px`;
          img.style.height = `${Math.round(h)}px`;
          img.style.maxWidth = "none";
        }
      });

      const contentCanvas = await html2canvasForPdf(html2canvas, contentEl);
      const { pdf } = assembleParecerPdf({
        PdfCtor: jsPDF,
        headerCanvas,
        footerCanvas,
        contentCanvas,
      });

      const url = pdf.output("bloburl");
      setPreviewUrl(url);
    } catch {
      setPreviewUrl(null);
    } finally {
      [headerEl, footerEl, contentEl].forEach((el) => {
        if (el && el.parentNode) el.parentNode.removeChild(el);
      });
      setPreviewLoading(false);
    }
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
  };

  // Carrega o último PDF Layout cadastrado
  useEffect(() => {
    const carregar = async () => {
      try {
        setLayoutLoading(true);
        const { data: layouts } = await pdfLayoutService.listar();
        if (!layouts || layouts.length === 0) {
          setLayoutInfo(null);
          return;
        }
        const ultimo = layouts[layouts.length - 1];
        const [headerResp, footerResp] = await Promise.all([
          pdfLayoutService.getHeaderImage(ultimo.id),
          pdfLayoutService.getFooterImage(ultimo.id),
        ]);
        const [headerBase64, footerBase64] = await Promise.all([
          toBase64(headerResp.data),
          toBase64(footerResp.data),
        ]);
        setLayoutInfo({ id: ultimo.id, headerBase64, footerBase64 });
      } catch {
        setLayoutErro(true);
      } finally {
        setLayoutLoading(false);
      }
    };
    carregar();
  }, []);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Highlight.configure({ multicolor: true }),
      ResizableImage.configure({ allowBase64: true, inline: false }),
      TextStyle,
      Color,
      FontSize,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Link.configure({ openOnClick: false }),
      Subscript,
      Superscript,
    ],
    content: CORPO_PADRAO,
    editorProps: {
      handlePaste(view, event) {
        const items = Array.from(event.clipboardData?.items || []);
        const imageItem = items.find((item) =>
          item.type.startsWith("image/")
        );
        if (!imageItem) return false;
        const file = imageItem.getAsFile();
        if (!file) return false;
        const reader = new FileReader();
        reader.onload = () => {
          const node = view.state.schema.nodes.image.create({
            src: reader.result,
          });
          view.dispatch(view.state.tr.replaceSelectionWith(node));
        };
        reader.readAsDataURL(file);
        return true;
      },
    },
  });

  // Reseta o conteúdo do editor para o corpo padrão (sem cabeçalho/rodapé)
  useEffect(() => {
    if (!editor || layoutLoading) return;
    editor.commands.setContent(CORPO_PADRAO);
  }, [editor, layoutLoading, layoutInfo]);

  return (
    <AnalistaRoute>
      <Box sx={{ backgroundColor: "background.default", minHeight: "100vh", py: { xs: 2, md: 3 } }}>
        <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
          {/* Cabeçalho */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              mb: 3,
              flexWrap: "wrap",
            }}
          >
            <RateReviewIcon
              sx={{
                fontSize: { xs: 28, md: 36 },
                color: theme.palette.primary.main,
              }}
            />
            <Typography
              variant="h5"
              component="h1"
              fontWeight={700}
              sx={{ fontSize: { xs: "1.2rem", sm: "1.5rem" } }}
            >
              Parecer Técnico
            </Typography>

            {/* Status do layout */}
            {layoutLoading && (
              <Chip
                icon={<CircularProgress size={14} />}
                label="Carregando layout..."
                size="small"
                variant="outlined"
              />
            )}
            {!layoutLoading && !layoutInfo && !layoutErro && (
              <Chip
                label="Nenhum layout encontrado"
                size="small"
                color="warning"
                variant="outlined"
              />
            )}
            {layoutErro && (
              <Chip
                label="Erro ao carregar layout"
                size="small"
                color="error"
                variant="outlined"
              />
            )}

            <Box sx={{ ml: { xs: 0, sm: "auto" }, width: { xs: "100%", sm: "auto" } }}>
              <Button
                variant="contained"
                startIcon={<PictureAsPdfIcon />}
                onClick={handlePreview}
                disabled={!editor || layoutLoading}
                sx={{ width: { xs: "100%", sm: "auto" } }}
              >
                PDF
              </Button>
            </Box>
          </Box>

          <Box sx={{ maxWidth: PARECER_PAGE_WIDTH_CSS, mx: "auto", width: "100%" }}>
            <MenuBar editor={editor} />
            <Paper
              elevation={4}
              sx={{
                width: "100%",
                px: { xs: "8mm", sm: "15mm", md: "25mm" },
                pt: layoutInfo?.headerBase64 ? 0 : { xs: "10mm", md: "20mm" },
                pb: layoutInfo?.footerBase64 ? 0 : { xs: "10mm", md: "20mm" },
                borderRadius: "0 0 4px 4px",
                backgroundColor: "white",
                boxSizing: "border-box",
                overflow: "hidden",
                "& .ProseMirror": {
                  ...PARECER_EDITOR_PROSEMIRROR_SX,
                  "&:focus, &:focus-visible": {
                    outline: `2px solid ${theme.palette.primary.main}`,
                    outlineOffset: "2px",
                  },
                },
              }}
            >
              {/* Cabeçalho estático */}
              {layoutInfo?.headerBase64 && (
                <Box
                  component="img"
                  src={layoutInfo.headerBase64}
                  alt="Cabeçalho"
                  sx={{
                    width: "100%",
                    display: "block",
                    pointerEvents: "none",
                    userSelect: "none",
                  }}
                />
              )}

              <EditorContent editor={editor} />

              {/* Rodapé estático */}
              {layoutInfo?.footerBase64 && (
                <Box
                  component="img"
                  src={layoutInfo.footerBase64}
                  alt="Rodapé"
                  sx={{
                    width: "100%",
                    display: "block",
                    pointerEvents: "none",
                    userSelect: "none",
                  }}
                />
              )}
            </Paper>
          </Box>
        </Container>

        <Dialog
          open={previewOpen}
          onClose={handleClosePreview}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              height: { xs: "100vh", sm: "90vh" },
              m: { xs: 0, sm: 2 },
              maxHeight: { xs: "100vh", sm: "90vh" },
            },
          }}
        >
          <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            Prévia do PDF
            <Button onClick={handleClosePreview} size="small">Fechar</Button>
          </DialogTitle>
          <DialogContent sx={{ p: 0, display: "flex", flexDirection: "column" }}>
            {previewLoading ? (
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", flex: 1, gap: 2 }}>
                <CircularProgress size={32} />
                <Typography color="text.secondary">Gerando prévia...</Typography>
              </Box>
            ) : previewUrl ? (
              <iframe
                src={previewUrl}
                title="Prévia PDF"
                style={{ flex: 1, border: "none", width: "100%" }}
              />
            ) : (
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", flex: 1 }}>
                <Typography color="error">Não foi possível gerar a prévia.</Typography>
              </Box>
            )}
          </DialogContent>
        </Dialog>
      </Box>
    </AnalistaRoute>
  );
};

export default ParecerTecnico;
